import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { priorityMeta } from '../../utils/priority';

// A custom dropdown for choosing a Nature of Complaint. Each option shows the
// nature name with a coloured priority badge, so the operator can see the
// priority a nature carries. Priority is NOT chosen here — it comes from the
// nature. Returns the selected nature id via onChange(id).
const NatureSelect = ({ natures = [], value, onChange, placeholder = 'Select a complaint type' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selected = useMemo(() => natures.find((n) => n._id === value) || null, [natures, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? natures.filter((n) => String(n.name || '').toLowerCase().includes(q)) : natures;
  }, [natures, query]);

  const Badge = ({ priority }) => {
    const m = priorityMeta(priority);
    return (
      <span
        className="d-inline-flex align-items-center gap-1"
        style={{ background: m.color, color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 700, flexShrink: 0 }}
      >
        <Icon icon={m.icon} style={{ fontSize: 12 }} /> {priority || 'Medium'}
      </span>
    );
  };

  const pick = (n) => { onChange(n._id); setOpen(false); setQuery(''); };

  return (
    <div className="position-relative" ref={ref}>
      <button
        type="button"
        className="form-select cpf-bg d-flex align-items-center justify-content-between text-start"
        onClick={() => setOpen((o) => !o)}
        style={{ gap: 8 }}
      >
        <span className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
          {selected ? (
            <>
              <span className="text-truncate">{selected.name}</span>
              <Badge priority={selected.priority_level} />
            </>
          ) : (
            <span className="text-muted">{placeholder}</span>
          )}
        </span>
      </button>

      {open && (
        <div
          className="position-absolute w-100 bg-white radius-8 shadow-lg"
          style={{ zIndex: 1000, top: 'calc(100% + 4px)', maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb' }}
        >
          <div className="p-2 border-bottom position-sticky top-0 bg-white" style={{ zIndex: 1 }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          {filtered.length === 0 ? (
            <div className="text-center text-secondary-light py-3" style={{ fontSize: 13 }}>No matches</div>
          ) : (
            filtered.map((n) => (
              <button
                type="button"
                key={n._id}
                className="d-flex align-items-center justify-content-between gap-2 w-100 px-12 py-8 border-0 bg-transparent text-start"
                style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(n)}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span className="text-truncate" style={{ color: '#1f2937' }}>{n.name}</span>
                <Badge priority={n.priority_level} />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NatureSelect;
